/**
 * Pattern Generator
 *
 * Generates LanguagePattern objects from CommandSchema + LanguageProfile.
 * This solves the pattern explosion problem by deriving patterns from
 * high-level definitions rather than hand-writing each one.
 */

import type { LanguagePattern, PatternToken, ExtractionRule } from '../types';
import type { LanguageProfile } from './language-profiles';
import type { CommandSchema, RoleSpec } from './command-schemas';
import { getDefinedSchemas } from './command-schemas';

// Import shared utilities
import { sortRolesByWordOrder } from '../parser/utils/role-positioning';
import { resolveMarkerForRole, schemaMarkerAlternatives } from '../parser/utils/marker-resolution';

// Import word-order-specific event handler generators
import {
  generateSOVEventHandlerPattern,
  generateSOVPatientFirstEventHandlerPattern,
  generateSOVPatientFirstSourceFrontedEventHandlerPattern,
  generateSOVPatientFirstWithDestEventHandlerPattern,
  generateSOVCompactEventHandlerPattern,
  generateSOVSimpleEventHandlerPattern,
  generateSOVTemporalEventHandlerPattern,
  generateSOVTwoRoleEventHandlerPattern,
  generateSOVTwoRoleDestFirstEventHandlerPattern,
  generateSOVTwoRoleEventLastEventHandlerPattern,
} from './event-handlers-sov';
import {
  generateVSOEventHandlerPattern,
  generateVSOVerbFirstEventHandlerPattern,
  generateVSOVerbFirstTwoRoleEventHandlerPattern,
  generateVSOTwoRoleEventHandlerPattern,
  generateVSONegatedEventHandlerPattern,
  generateVSOProcliticEventHandlerPattern,
} from './event-handlers-vso';

// Note: languageProfiles is no longer imported here.
// Pattern generation for specific languages uses the registry instead.

// Import registry functions - this is safe because:
// 1. Registry doesn't import pattern-generator
// 2. The circular path is: pattern-generator -> registry -> pattern-generator
//    But registry only uses setPatternGenerator which is exported, not module-level code
import {
  getRegisteredLanguages as registryGetLanguages,
  tryGetProfile as registryTryGetProfile,
} from '../registry';

function getAllRegisteredProfiles(): LanguageProfile[] {
  const languages = registryGetLanguages();
  return languages
    .map((lang: string) => registryTryGetProfile(lang))
    .filter((p): p is LanguageProfile => p !== undefined);
}

// =============================================================================
// Pattern Generator
// =============================================================================

/**
 * Configuration for pattern generation.
 */
export interface GeneratorConfig {
  /** Base priority for generated patterns (higher = checked first) */
  basePriority?: number;
  /** Whether to generate simple patterns (without optional roles) */
  generateSimpleVariants?: boolean;
  /** Whether to generate alternative keyword patterns */
  generateAlternatives?: boolean;
  /**
   * Whether to generate verb-first fallback patterns for SOV languages
   * (`トグル .active` alongside the canonical `.active を トグル`). Default on.
   * These are strictly lower priority than the canonical patterns, so native
   * SOV order always wins; this only accepts bilingual/code-switched input.
   */
  generateVerbFirstVariants?: boolean;
}

const defaultConfig: GeneratorConfig = {
  basePriority: 100,
  generateSimpleVariants: true,
  generateAlternatives: true,
};

/**
 * Generate a pattern for a command in a specific language.
 */
export function generatePattern(
  schema: CommandSchema,
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern {
  const id = `${schema.action}-${profile.code}-generated`;
  const priority = config.basePriority ?? 100;

  // Get keyword translation
  const keyword = profile.keywords[schema.action];
  if (!keyword) {
    throw new Error(`No keyword translation for '${schema.action}' in ${profile.code}`);
  }

  // Build tokens based on word order
  const tokens = buildTokens(schema, profile, keyword);

  // Build extraction rules with defaults for optional roles
  // This ensures defaults are applied even when optional groups don't match
  const extraction = buildExtractionRulesWithDefaults(schema, profile);

  // Build template format string (for documentation)
  const format = buildFormatString(schema, profile, keyword);

  return {
    id,
    language: profile.code,
    command: schema.action,
    priority,
    template: {
      format,
      tokens,
    },
    extraction,
  };
}

/**
 * Generate a simple variant pattern (without optional roles).
 */
export function generateSimplePattern(
  schema: CommandSchema,
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern | null {
  // Only generate simple variant if there are optional roles
  const optionalRoles = schema.roles.filter(r => !r.required);
  if (optionalRoles.length === 0) {
    return null;
  }

  const requiredRoles = schema.roles.filter(r => r.required);
  const simpleSchema: CommandSchema = {
    ...schema,
    roles: requiredRoles,
  };

  const pattern = generatePattern(simpleSchema, profile, config);

  // Adjust for simple variant
  return {
    ...pattern,
    id: `${schema.action}-${profile.code}-generated-simple`,
    priority: (config.basePriority ?? 100) - 5, // Lower priority than full pattern (was -10)
    extraction: buildExtractionRulesWithDefaults(schema, profile),
  };
}

/**
 * Generate a verb-first fallback pattern for an SOV language.
 *
 * Canonical SOV order is patient-then-verb (`.active を トグル`). Bilingual and
 * code-switching authors often write the English/loanword verb first
 * (`トグル .active`). This emits `[verb] [required role(s), UNMARKED]` at a lower
 * priority so the canonical pattern always wins for native input — the
 * verb-first form only matches when the input genuinely starts with the verb.
 *
 * Kept deliberately conservative: only commands with a single required role
 * (patient-focused: toggle/add/remove/show/hide/…) get this fallback, which
 * keeps the unmarked match unambiguous. Returns null otherwise.
 */
export function generateVerbFirstPattern(
  schema: CommandSchema,
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern | null {
  if (profile.wordOrder !== 'SOV') return null;

  const requiredRoles = schema.roles.filter(r => r.required);
  if (requiredRoles.length !== 1) return null;

  const keyword = profile.keywords[schema.action];
  if (!keyword) return null;

  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };

  // Verb first, then the required role(s) with NO marker (the marker is what
  // disambiguates roles in canonical SOV; verb-first input typically omits it).
  // A valuePrefixLiteral (rolePrefixLiteralVariants clones only) still precedes
  // its role's value — the SOV transformer renders `移動 url "/page" に`
  // verb-first, which is exactly this shape (trailing particle tolerated).
  const roleTokens: PatternToken[] = requiredRoles.flatMap(r => {
    const prefix = r.valuePrefixLiteral?.[profile.code];
    const roleToken: PatternToken = {
      type: 'role',
      role: r.role,
      optional: false,
      expectedTypes: r.expectedTypes,
    };
    return prefix ? [{ type: 'literal', value: prefix } as PatternToken, roleToken] : [roleToken];
  });

  return {
    id: `${schema.action}-${profile.code}-generated-verb-first`,
    language: profile.code,
    command: schema.action,
    // Strictly below the canonical (basePriority) and simple (basePriority - 5)
    // patterns so native order is always preferred.
    priority: (config.basePriority ?? 100) - 20,
    template: {
      format: `${keyword.primary} <${requiredRoles[0].role}>`,
      tokens: [verbToken, ...roleTokens],
    },
    extraction: buildExtractionRulesWithDefaults(schema, profile),
  };
}

/**
 * Generate all pattern variants for a command in a language.
 */
export function generatePatternVariants(
  schema: CommandSchema,
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern[] {
  const patterns: LanguagePattern[] = [];

  // Main pattern
  patterns.push(generatePattern(schema, profile, config));

  // Simple variant (without optional roles)
  if (config.generateSimpleVariants) {
    const simple = generateSimplePattern(schema, profile, config);
    if (simple) {
      patterns.push(simple);
    }
  }

  // Omit-role variants: an EXTRA pattern per schema-listed role with that
  // role (and its marker phrase) removed entirely. Covers a REQUIRED role
  // whose phrase is legitimately absent in valid hyperscript (transition's
  // goal: `transition *max-height over 300ms`, slide-toggle) — making the
  // role optional inside the main pattern is the verified skippable-group
  // re-binding regression (see the transition goal NOTE); a separate variant
  // has no skippable group at all. Priority sits below the simple variant so
  // any text carrying the role phrase always prefers the fuller patterns.
  for (const omitted of schema.omitRoleVariants ?? []) {
    const variantSchema: CommandSchema = {
      ...schema,
      roles: schema.roles.filter(r => r.role !== omitted),
    };
    const variant = generatePattern(variantSchema, profile, config);
    patterns.push({
      ...variant,
      id: `${schema.action}-${profile.code}-generated-no-${omitted}`,
      priority: (config.basePriority ?? 100) - 8,
    });
  }

  // Widened-type variants: an EXTRA pattern per entry whose named role accepts a
  // wider set of value types. Deliberately BELOW every command pattern, so it is
  // inert on any span a real command can claim — see the schema field's doc for
  // the ko `transition opacity to 0` collision that rules out widening the main
  // pattern in place.
  for (const widen of schema.widenTypeVariants ?? []) {
    if (widen.excludeLanguages?.includes(profile.code)) continue;
    const cloneSchema: CommandSchema = {
      ...schema,
      roles: schema.roles.map(r =>
        r.role === widen.role ? { ...r, expectedTypes: [...widen.types] } : r
      ),
    };
    const main = generatePattern(cloneSchema, profile, config);
    patterns.push({
      ...main,
      id: `${schema.action}-${profile.code}-generated-${widen.idSuffix}`,
      priority: (config.basePriority ?? 100) - (widen.priorityDelta ?? 15),
    });
  }

  // Verb-first fallback for SOV languages (lower priority than the above).
  if (config.generateVerbFirstVariants !== false) {
    const verbFirst = generateVerbFirstPattern(schema, profile, config);
    if (verbFirst) {
      patterns.push(verbFirst);
    }
  }

  // Value-prefix-literal variants: an EXTRA pattern per entry with a REQUIRED
  // literal keyword immediately before the named role's value (`go [to] url
  // {destination}`). Higher priority than the counterpart pattern, but inert
  // unless the keyword is present — base patterns above stay byte-identical,
  // so forms riding on them (`go back`) cannot regress. The matched keyword is
  // recorded as a fixed-value literal in the variant's methodCarrier role.
  for (const v of schema.rolePrefixLiteralVariants ?? []) {
    const literal = v.literal[profile.code];
    if (!literal) continue;
    const { rolePrefixLiteralVariants: _omitted, ...baseSchema } = schema;
    const cloneSchema: CommandSchema = {
      ...baseSchema,
      roles: schema.roles.map(r =>
        r.role === v.role ? { ...r, valuePrefixLiteral: { [profile.code]: literal } } : r
      ),
    };
    const delta = v.priorityDelta ?? 5;
    const carrier: Record<string, ExtractionRule> = v.methodCarrier
      ? { [v.methodCarrier]: { value: literal } }
      : {};
    const main = generatePattern(cloneSchema, profile, config);
    patterns.push({
      ...main,
      id: `${schema.action}-${profile.code}-generated-${v.idSuffix}`,
      priority: (config.basePriority ?? 100) + delta,
      extraction: { ...main.extraction, ...carrier },
    });
    if (config.generateVerbFirstVariants !== false) {
      const verbFirstUrl = generateVerbFirstPattern(cloneSchema, profile, config);
      if (verbFirstUrl) {
        patterns.push({
          ...verbFirstUrl,
          id: `${schema.action}-${profile.code}-generated-verb-first-${v.idSuffix}`,
          priority: (config.basePriority ?? 100) - 20 + delta,
          extraction: { ...verbFirstUrl.extraction, ...carrier },
        });
      }
    }
  }

  return patterns;
}

/**
 * Generate patterns for all commands in a specific language.
 */
export function generatePatternsForLanguage(
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern[] {
  const patterns: LanguagePattern[] = [];
  const schemas = getDefinedSchemas();

  for (const schema of schemas) {
    // Skip if no keyword translation exists
    if (!profile.keywords[schema.action]) {
      continue;
    }

    // Generate simple command patterns
    const variants = generatePatternVariants(schema, profile, config);
    patterns.push(...variants);

    // Generate event handler patterns (on [event] [command] [patient])
    // Only generate for languages with eventHandler configuration
    if (profile.eventHandler?.eventMarker) {
      const eventHandlerPatterns = generateEventHandlerPatterns(schema, profile, config);
      patterns.push(...eventHandlerPatterns);
    }
  }

  return patterns;
}

/**
 * Generate patterns for a command across specified profiles.
 *
 * @param schema Command schema to generate patterns for
 * @param profiles Array of language profiles to generate patterns for (defaults to all registered)
 * @param config Generator configuration
 */
export function generatePatternsForCommand(
  schema: CommandSchema,
  profiles?: LanguageProfile[],
  config: GeneratorConfig = defaultConfig
): LanguagePattern[] {
  const patterns: LanguagePattern[] = [];

  // If no profiles provided, use all registered profiles
  const profilesToUse = profiles ?? getAllRegisteredProfiles();

  for (const profile of profilesToUse) {
    // Skip if no keyword translation exists
    if (!profile.keywords[schema.action]) {
      continue;
    }

    const variants = generatePatternVariants(schema, profile, config);
    patterns.push(...variants);
  }

  return patterns;
}

/**
 * Generate all patterns for all commands across specified profiles.
 *
 * @param profiles Array of language profiles to generate patterns for (defaults to all registered)
 * @param config Generator configuration
 */
export function generateAllPatterns(
  profiles?: LanguageProfile[],
  config: GeneratorConfig = defaultConfig
): LanguagePattern[] {
  const patterns: LanguagePattern[] = [];

  // If no profiles provided, use all registered profiles
  const profilesToUse = profiles ?? getAllRegisteredProfiles();

  for (const profile of profilesToUse) {
    const langPatterns = generatePatternsForLanguage(profile, config);
    patterns.push(...langPatterns);
  }

  return patterns;
}

/**
 * Generate event handler patterns for a command in a specific language.
 *
 * Creates patterns that wrap commands with event handlers (e.g., "on click toggle .active").
 * Automatically handles SOV, SVO, and VSO word orders based on language profile.
 *
 * @param commandSchema - The command to wrap (toggle, add, remove, etc.)
 * @param profile - Language profile with eventHandler configuration
 * @param config - Generator configuration
 * @returns Array of event handler patterns (empty if profile lacks eventHandler config)
 */
export function generateEventHandlerPatterns(
  commandSchema: CommandSchema,
  profile: LanguageProfile,
  config: GeneratorConfig = defaultConfig
): LanguagePattern[] {
  // Only generate if profile has eventHandler configuration
  if (!profile.eventHandler || !profile.eventHandler.eventMarker) {
    return [];
  }

  const patterns: LanguagePattern[] = [];
  const eventMarker = profile.eventHandler.eventMarker;
  const keyword = profile.keywords[commandSchema.action];

  if (!keyword) {
    return []; // No translation for this command
  }

  // Roleless (bareKeyword) commands take no patient, but every fused shape
  // below hardwires a required {patient} role — fusing would swallow the next
  // token (a `then` connective, the following command's head) as junk patient.
  // They parse fine via the standard event pattern + bare command in the body.
  if (commandSchema.roles.length === 0) {
    return [];
  }

  // `js` is never fused either, for the opposite reason: its one role is not a
  // value, it is an opaque span of raw JavaScript that runs to the block's `end`.
  // No pattern slot can capture that — the matcher tokenizes and re-spaces it,
  // and `console.log("x")` comes back as `console .log ( "x" )`. The clause walk
  // has the two purpose-built consumers (`consumeJsBlock` /
  // `consumeVerbFinalJsBlock`), and a fused pattern's only effect is to reach the
  // verb FIRST and strand the body.
  //
  // It bit exactly where the marker shapes happened to line up: bn `ক্লিক তে জেএস`
  // and hi `click पर जेएस` match `{event} <marker> <verb>` outright, while ja
  // `クリック を で JS実行` and ko `클릭 을 에 JS실행` carry a second marker that
  // makes the same pattern miss — so ja/ko/tr/qu already took the clause walk and
  // bn/hi did not. That is a coincidence of surface, not a design.
  if (commandSchema.action === 'js') {
    return [];
  }

  // Check if this is a two-role command (like put, set)
  const requiredRoles = commandSchema.roles.filter(r => r.required);
  const hasTwoRequiredRoles = requiredRoles.length === 2;

  // Generate pattern based on word order
  if (profile.wordOrder === 'SOV') {
    if (hasTwoRequiredRoles) {
      // Two-role SOV pattern for put/set commands (event-first)
      // Japanese put: 入力 で "test" を #output に 入れる
      // Korean set: 변경 할 때 x 를 10 에 설정
      patterns.push(
        generateSOVTwoRoleEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );

      // Two-role SOV pattern (destination-first variant)
      // Bengali set: @disabled কে ক্লিক এ সেট সত্য তে
      // Quechua set: x ta ñitiy pi 10 man churay
      patterns.push(
        generateSOVTwoRoleDestFirstEventHandlerPattern(
          commandSchema,
          profile,
          keyword,
          eventMarker,
          config
        )
      );

      // Two-role SOV pattern (event-last variant) — the event phrase trails the
      // verb: Japanese put `"Done!" を 私 に 置く クリック で` (S4 put-content-basic).
      patterns.push(
        generateSOVTwoRoleEventLastEventHandlerPattern(
          commandSchema,
          profile,
          keyword,
          eventMarker,
          config
        )
      );
    } else {
      // SOV: [event] [eventMarker] [destination? destMarker?] [patient] [patientMarker] [verb]
      // Japanese: クリック で #button の .active を 切り替え
      // Korean: 클릭 할 때 #button 의 .active 를 토글
      // Turkish: tıklama da #button ın .active i değiştir
      patterns.push(
        generateSOVEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );

      // Patient-first SOV variants: patient before event (common in Bengali, Quechua)
      // Bengali: .active কে ক্লিক এ টগল  (patient + event + verb)
      // Quechua: .highlight ta noqa man ñitiy pi yapay  (patient + dest + event + verb)
      // Both orders are valid in SOV languages depending on topic/focus.

      // Variant 1: Simple (no destination) — higher priority
      patterns.push(
        generateSOVPatientFirstEventHandlerPattern(
          commandSchema,
          profile,
          keyword,
          eventMarker,
          config
        )
      );

      // Variant 1b: Source-fronted (source phrase between patient and event,
      // both required) — qu's canonical order for source-carrying commands
      // (take/remove). Self-gates: null unless the schema has a source role
      // with a usable marker distinct from the event/patient markers.
      const sourceFronted = generateSOVPatientFirstSourceFrontedEventHandlerPattern(
        commandSchema,
        profile,
        keyword,
        eventMarker,
        config
      );
      if (sourceFronted) patterns.push(sourceFronted);

      // Variant 2: With destination (required, not optional) — lower priority
      // Only add if destination marker differs from event marker to avoid collision
      const destMarkerCheck = profile.roleMarkers.destination;
      if (destMarkerCheck && destMarkerCheck.primary !== eventMarker.primary) {
        patterns.push(
          generateSOVPatientFirstWithDestEventHandlerPattern(
            commandSchema,
            profile,
            keyword,
            eventMarker,
            config
          )
        );
      }

      // For multi-word event markers with no-space alternatives (Korean compact forms),
      // also generate a pattern that accepts the compact form
      // Example: 클릭할때 .active를토글 (할때 as single token)
      const markerWords = eventMarker.primary.split(/\s+/);
      const hasNoSpaceAlternative = eventMarker.alternatives?.some(
        alt => !alt.includes(' ') && alt.length > 1
      );
      if (markerWords.length > 1 && hasNoSpaceAlternative) {
        patterns.push(
          generateSOVCompactEventHandlerPattern(
            commandSchema,
            profile,
            keyword,
            eventMarker,
            config
          )
        );
      }

      // Add simple pattern (no patient required, defaults to 'me')
      // Supports: クリック で 増加 (click on increment)
      patterns.push(
        generateSOVSimpleEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );

      // Add temporal pattern if temporalMarkers defined
      // Supports: クリック の 時 .active を 切り替え (click's time toggle .active)
      const temporalPattern = generateSOVTemporalEventHandlerPattern(
        commandSchema,
        profile,
        keyword,
        config
      );
      if (temporalPattern) {
        patterns.push(temporalPattern);
      }
    }
  } else if (profile.wordOrder === 'VSO') {
    if (hasTwoRequiredRoles) {
      // Two-role VSO pattern for put/set commands (event-first)
      // Arabic put: عند الإدخال ضع "test" في #output
      patterns.push(
        generateVSOTwoRoleEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );

      // Two-role VSO verb-first variant (event handler at end)
      // Tagalog: itakda aking *background sa "red" kapag click
      patterns.push(
        generateVSOVerbFirstTwoRoleEventHandlerPattern(
          commandSchema,
          profile,
          keyword,
          eventMarker,
          config
        )
      );
    } else {
      // VSO: [eventMarker] [event] [verb] [patient] [على destination?]
      // Arabic: عند النقر بدّل .active على #button
      patterns.push(
        generateVSOEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );

      // VSO verb-first variant: [verb] [patient] [eventMarker] [event]
      // Tagalog: alisin ako kapag click (remove me on click)
      // Tagalog: palitan .active kapag click (toggle .active on click)
      patterns.push(
        generateVSOVerbFirstEventHandlerPattern(
          commandSchema,
          profile,
          keyword,
          eventMarker,
          config
        )
      );

      // Add negated event pattern variant for languages with negation markers
      // Pattern: [eventMarker] [negation] [event] [verb] [patient]
      // Example: عند عدم التركيز أخف #tooltip = "on blur hide #tooltip"
      if (profile.eventHandler?.negationMarker) {
        patterns.push(
          generateVSONegatedEventHandlerPattern(
            commandSchema,
            profile,
            keyword,
            eventMarker,
            config
          )
        );
      }

      // Add proclitic-prefixed pattern variant for Arabic
      // Pattern: [proclitic]? [event] [verb] [patient]
      // Example: والنقر بدّل .active (and click toggle .active)
      if (profile.tokenization?.prefixes) {
        patterns.push(
          generateVSOProcliticEventHandlerPattern(commandSchema, profile, keyword, config)
        );
      }
    }
  } else {
    // SVO: Use VSO pattern structure for event handlers
    if (hasTwoRequiredRoles) {
      patterns.push(
        generateVSOTwoRoleEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );
    } else {
      patterns.push(
        generateVSOEventHandlerPattern(commandSchema, profile, keyword, eventMarker, config)
      );
    }
  }

  return patterns;
}

// =============================================================================
// Token Building
// =============================================================================

/**
 * Build pattern tokens based on word order.
 */
function buildTokens(
  schema: CommandSchema,
  profile: LanguageProfile,
  keyword: { primary: string; alternatives?: string[] }
): PatternToken[] {
  const tokens: PatternToken[] = [];

  // Get verb token
  const verbToken: PatternToken = keyword.alternatives
    ? { type: 'literal', value: keyword.primary, alternatives: keyword.alternatives }
    : { type: 'literal', value: keyword.primary };

  // Get role tokens sorted by position
  const roleTokens = buildRoleTokens(schema, profile);

  // Arrange based on word order
  switch (profile.wordOrder) {
    case 'SVO':
      // Verb first, then roles in order
      tokens.push(verbToken);
      tokens.push(...roleTokens);
      break;

    case 'SOV':
      // Roles first (reversed for SOV), then verb
      tokens.push(...roleTokens);
      tokens.push(verbToken);
      break;

    case 'VSO':
      // Verb first, then subject, then object
      tokens.push(verbToken);
      tokens.push(...roleTokens);
      break;

    default:
      // Default to SVO
      tokens.push(verbToken);
      tokens.push(...roleTokens);
  }

  return collapseAdjacentLiterals(tokens);
}

/**
 * Collapse adjacent identical literal tokens into one.
 *
 * For the event-handler trigger (`on`) schema, a handful of languages use the
 * same word for the trigger keyword (the `on` verb) and the event role's marker —
 * bn `তে`, hi `पर`, th `เมื่อ`. `buildTokens` then emits it twice (`<event> তে তে`),
 * which the renderer prints verbatim; on re-parse the duplicate pairs with the
 * following token as a phantom command (e.g. `তে আমি` → a spurious `on me`). No
 * trigger pattern needs to match the same literal twice in a row, so fold the
 * duplicate, preserving any `alternatives` from either side. Verified to affect
 * only the `on` patterns of bn/hi/th — no other command/language has adjacent
 * duplicate literals. See MULTILINGUAL_BEHAVIORS_PLAN.md Phase 2.
 */
function collapseAdjacentLiterals(tokens: PatternToken[]): PatternToken[] {
  const out: PatternToken[] = [];
  for (const tok of tokens) {
    const prev = out[out.length - 1];
    if (prev && prev.type === 'literal' && tok.type === 'literal' && prev.value === tok.value) {
      const alts = [...(prev.alternatives ?? []), ...(tok.alternatives ?? [])];
      if (alts.length) out[out.length - 1] = { ...prev, alternatives: [...new Set(alts)] };
      continue;
    }
    out.push(tok);
  }
  return out;
}

/**
 * Build tokens for roles.
 */
function buildRoleTokens(schema: CommandSchema, profile: LanguageProfile): PatternToken[] {
  const tokens: PatternToken[] = [];

  // Sort roles by position using shared utility
  const sortedRoles = sortRolesByWordOrder(schema.roles, profile.wordOrder);

  for (const roleSpec of sortedRoles) {
    const roleToken = buildRoleToken(roleSpec, profile);

    if (!roleSpec.required) {
      // Wrap optional roles in a group
      tokens.push({
        type: 'group',
        optional: true,
        tokens: roleToken,
      });
    } else {
      tokens.push(...roleToken);
    }
  }

  return tokens;
}

/**
 * Build token(s) for a single role.
 *
 * Exported so the fused event-handler generators can build a wrapped command's
 * optional-role slots from the very same code the standalone command patterns
 * use — a hand-rolled copy there is what let the two drift apart in the first
 * place (see `appendRemainingOptionalRoles` in event-handlers-sov.ts).
 */
export function buildRoleToken(roleSpec: RoleSpec, profile: LanguageProfile): PatternToken[] {
  const tokens: PatternToken[] = [];

  // Check for command-specific marker override first
  const overrideMarker = roleSpec.markerOverride?.[profile.code];
  const defaultMarker = profile.roleMarkers[roleSpec.role];

  // A role whose renderOverride is the empty string parses WITH its marker but
  // renders WITHOUT it (e.g. fetch's `from`: `fetch from /api` must parse, yet
  // canonical _hyperscript rejects `fetch from "/api"` while accepting
  // `fetch "/api"`). Tag the emitted marker literal so the renderer skips it;
  // the pattern matcher ignores the flag, so parsing is unchanged.
  const suppressMarker = roleSpec.renderOverride?.[profile.code] === '';

  // Role value token
  const roleValueToken: PatternToken = {
    type: 'role',
    role: roleSpec.role,
    optional: !roleSpec.required,
    expectedTypes: roleSpec.expectedTypes,
    // The matcher reads this too — a `'keyword'`-shaped slot holds a fixed
    // keyword phrase (`using view transition`) and must not be skipped by the
    // trailing-slot verb guard.
    ...(roleSpec.valueShape !== undefined ? { valueShape: roleSpec.valueShape } : {}),
  };

  // Required literal immediately before the role VALUE (rolePrefixLiteralVariants
  // clones only): `[to] url {destination}`, `url {destination} に`. Deliberately
  // NOT an optional group — the variant must only match when the keyword is
  // present, so base patterns keep winning everywhere else.
  const prefixLiteral = roleSpec.valuePrefixLiteral?.[profile.code];
  const pushPrefixed = (): void => {
    if (prefixLiteral) tokens.push({ type: 'literal', value: prefixLiteral });
    tokens.push(roleValueToken);
  };

  // Use override marker if available, otherwise use default
  if (overrideMarker !== undefined) {
    // Command-specific marker override. Multi-word markers (e.g. "partials in",
    // "with title") split into one literal token per word so the matcher
    // consumes them sequentially. A per-command markerOptional wraps each word
    // in an optional group (`go to /page` and `go back` both parse — the
    // render side drops the preposition, so the parse side can't require it).
    const markerWords = overrideMarker ? overrideMarker.split(/\s+/).filter(Boolean) : [];
    // Where the profile carries no RoleMarker for this role there is nothing to
    // take a side from, and 'before' is wrong in a verb-final language: ja
    // rendered `opacity を に 0` and bn `opacity কে তে 0`, the override marker
    // stranded AHEAD of its own value. Default to 'after' for SOV.
    //
    // EXEMPT a PASSTHROUGH override — one whose marker is the same string en
    // uses (`scope`, `manner`). Those are English words carried verbatim into
    // every language, so they keep English's prepositional side; without the
    // exemption ja/ko/qu lose `set`'s scope.
    const isPassthroughOverride =
      overrideMarker !== undefined && overrideMarker === roleSpec.markerOverride?.en;
    const position =
      defaultMarker?.position ??
      (profile.wordOrder === 'SOV' && !isPassthroughOverride ? 'after' : 'before');
    const optionalMarker = roleSpec.markerOptional?.[profile.code] === true;
    // Single-word overrides also accept every schema-declared alternative
    // (markerLegacy ∪ markerVariants), so correcting an override's marker does
    // not stop the previous one parsing, and declaring an override does not
    // silently delete that language's variants. schemaMarkerAlternatives holds
    // variants out for a methodCarrier role (`put`'s before/after) — see its doc.
    const pushWord = (word: string): void => {
      const alternatives =
        markerWords.length === 1
          ? (schemaMarkerAlternatives(roleSpec, profile.code, word) ?? [])
          : [];
      const literal: PatternToken = {
        type: 'literal',
        value: word,
        ...(alternatives.length ? { alternatives } : {}),
        ...(suppressMarker ? { renderSuppress: true } : {}),
      };
      tokens.push(optionalMarker ? { type: 'group', optional: true, tokens: [literal] } : literal);
    };
    if (position === 'before') {
      for (const word of markerWords) pushWord(word);
      pushPrefixed();
    } else {
      pushPrefixed();
      for (const word of markerWords) pushWord(word);
    }
  } else if (defaultMarker) {
    // When the profile allows colloquial marker-dropping, the marker becomes an
    // optional group so `.active değiştir` parses as well as `.active i değiştir`.
    // The marked form still matches the marker literal directly (higher
    // confidence); the unmarked form falls back through the optional group.
    // The schema's markerVariants/markerLegacy merge in as extra alternatives:
    // the transformer can render a role with a particle the profile default
    // doesn't cover (zh 把 before go's destination in `go back`).
    const asMarker = (): PatternToken => {
      const alternatives = [
        ...new Set([
          ...(defaultMarker.alternatives ?? []),
          ...(schemaMarkerAlternatives(roleSpec, profile.code, defaultMarker.primary) ?? []),
        ]),
      ].filter(a => a !== defaultMarker.primary);
      return {
        type: 'literal',
        value: defaultMarker.primary,
        ...(alternatives.length ? { alternatives } : {}),
        ...(suppressMarker ? { renderSuppress: true } : {}),
      };
    };
    const pushMarker = (marker: PatternToken): void => {
      if (!profile.markersOptional && !roleSpec.markerOptional?.[profile.code]) {
        tokens.push(marker);
        return;
      }
      // A profile-wide `markersOptional` says the marker may be DROPPED
      // colloquially, not that it is absent from canonical output — so the group
      // still renders. A per-role `markerOptional` is the opposite: the render
      // side genuinely omits that marker (`go to /page` vs `go back`), so it
      // stays render-optional too.
      tokens.push({
        type: 'group',
        optional: true,
        ...(profile.markersOptional && !roleSpec.markerOptional?.[profile.code]
          ? { renderRequired: true }
          : {}),
        tokens: [marker],
      });
    };
    if (defaultMarker.position === 'before') {
      // Preposition: "on #button"
      if (defaultMarker.primary) {
        pushMarker(asMarker());
      }
      pushPrefixed();
    } else {
      // Postposition/particle: "#button に"
      pushPrefixed();
      pushMarker(asMarker());
    }
  } else {
    // No marker, just the role value
    pushPrefixed();
  }

  return tokens;
}

// =============================================================================
// Extraction Rules Building
// =============================================================================

/**
 * Build extraction rules for a pattern.
 */
function buildExtractionRules(
  schema: CommandSchema,
  profile: LanguageProfile
): Record<string, ExtractionRule> {
  const rules: Record<string, ExtractionRule> = {};

  for (const roleSpec of schema.roles) {
    // Check for command-specific marker override first
    const overrideMarker = roleSpec.markerOverride?.[profile.code];
    const defaultMarker = profile.roleMarkers[roleSpec.role];

    if (roleSpec.valuePrefixLiteral?.[profile.code]) {
      // The prefix literal sits immediately before the value on every surface,
      // so it is the extraction anchor (push/replace's proven `url` shape) —
      // it wins over the role's own marker (`to`), which may be optional/absent.
      rules[roleSpec.role] = { marker: roleSpec.valuePrefixLiteral[profile.code] };
    } else if (overrideMarker !== undefined) {
      // Use the override marker, accepting the schema's declared alternatives
      // so a corrected marker does not stop the previous one parsing and an
      // override does not delete that language's variants (mirrors
      // buildRoleToken; single-word overrides only, as a multi-word marker has
      // no single token to swap).
      if (!overrideMarker) {
        rules[roleSpec.role] = {};
      } else {
        const isSingleWord = !/\s/.test(overrideMarker.trim());
        const markerAlternatives = isSingleWord
          ? (schemaMarkerAlternatives(roleSpec, profile.code, overrideMarker) ?? [])
          : [];
        rules[roleSpec.role] = markerAlternatives.length
          ? { marker: overrideMarker, markerAlternatives }
          : { marker: overrideMarker };
      }
    } else if (defaultMarker && defaultMarker.primary) {
      // Merge the schema's declared alternatives (mirrors buildRoleToken)
      const markerAlternatives = [
        ...new Set([
          ...(defaultMarker.alternatives ?? []),
          ...(schemaMarkerAlternatives(roleSpec, profile.code, defaultMarker.primary) ?? []),
        ]),
      ].filter(a => a !== defaultMarker.primary);
      rules[roleSpec.role] = markerAlternatives.length
        ? { marker: defaultMarker.primary, markerAlternatives }
        : { marker: defaultMarker.primary };
    } else {
      rules[roleSpec.role] = {};
    }
  }

  return rules;
}

/**
 * Build extraction rules with defaults for optional roles.
 */
function buildExtractionRulesWithDefaults(
  schema: CommandSchema,
  profile: LanguageProfile
): Record<string, ExtractionRule> {
  const baseRules = buildExtractionRules(schema, profile);
  const rules: Record<string, ExtractionRule> = {};

  // Copy base rules and add defaults for optional roles
  for (const roleSpec of schema.roles) {
    const baseRule = baseRules[roleSpec.role] || {};

    if (!roleSpec.required && roleSpec.default) {
      rules[roleSpec.role] = { ...baseRule, default: roleSpec.default };
    } else {
      rules[roleSpec.role] = baseRule;
    }
  }

  return rules;
}

// =============================================================================
// Format String Building
// =============================================================================

/**
 * Build a human-readable format string for documentation.
 */
function buildFormatString(
  schema: CommandSchema,
  profile: LanguageProfile,
  keyword: { primary: string }
): string {
  const parts: string[] = [];

  // Sort roles by position using shared utility
  const sortedRoles = sortRolesByWordOrder(schema.roles, profile.wordOrder);

  // Build role parts
  const roleParts = sortedRoles.map(roleSpec => {
    // Use shared marker resolution utility
    const resolved = resolveMarkerForRole(roleSpec, profile);
    let part = '';

    if (resolved && resolved.primary) {
      // Has a marker
      if (resolved.position === 'before') {
        part = `${resolved.primary} {${roleSpec.role}}`;
      } else {
        part = `{${roleSpec.role}} ${resolved.primary}`;
      }
    } else {
      // No marker
      part = `{${roleSpec.role}}`;
    }

    return roleSpec.required ? part : `[${part}]`;
  });

  // Arrange based on word order
  switch (profile.wordOrder) {
    case 'SVO':
    case 'VSO':
      parts.push(keyword.primary, ...roleParts);
      break;
    case 'SOV':
      parts.push(...roleParts, keyword.primary);
      break;
    default:
      parts.push(keyword.primary, ...roleParts);
  }

  return parts.join(' ');
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get a summary of what patterns can be generated.
 * Note: This requires the registry to have languages registered.
 */
export function getGeneratorSummary(): {
  languages: string[];
  commands: string[];
  totalPatterns: number;
} {
  const languages = registryGetLanguages();
  const commands = getDefinedSchemas().map(s => s.action);

  // Estimate total patterns (2 variants per command per language)
  let totalPatterns = 0;
  for (const lang of languages) {
    const profile = registryTryGetProfile(lang);
    if (profile) {
      for (const schema of getDefinedSchemas()) {
        if (profile.keywords[schema.action]) {
          totalPatterns += 2; // Full + simple variant
        }
      }
    }
  }

  return { languages, commands, totalPatterns };
}

/**
 * Validate that all required keywords exist for a language.
 */
export function validateLanguageKeywords(
  profile: LanguageProfile,
  schemas: CommandSchema[] = getDefinedSchemas()
): { missing: string[]; available: string[] } {
  const missing: string[] = [];
  const available: string[] = [];

  for (const schema of schemas) {
    if (profile.keywords[schema.action]) {
      available.push(schema.action);
    } else {
      missing.push(schema.action);
    }
  }

  return { missing, available };
}
